/**
 * 座標は 0-based 半開 `[start, end)`（§3.2）。表示だけ 1-based。
 * 環状の原点ラップは `end > genomeLength` かつ `end - start < genomeLength`（§3.3）。
 */

export type Topology = 'linear' | 'circular';

export interface Interval {
  start: number;
  end: number;
}

export function intervalLength(interval: Interval): number {
  return Math.max(0, interval.end - interval.start);
}

/** 原点ラップしているか（§3.3） */
export function isOriginWrapped(interval: Interval, genomeLength: number): boolean {
  return (
    genomeLength > 0 &&
    interval.end > genomeLength &&
    interval.end - interval.start < genomeLength
  );
}

/** 座標を分子内に丸める。環状は剰余、線形はクランプ。 */
export function normalizeBase(position: number, genomeLength: number, topology: Topology): number {
  if (genomeLength <= 0) return 0;
  if (topology === 'circular') {
    const mod = position % genomeLength;
    return mod < 0 ? mod + genomeLength : mod;
  }
  return Math.min(Math.max(position, 0), genomeLength);
}

/**
 * 区間を分子内の実体スパンへ分解する。原点ラップは尾（`start..length`）と頭（`0..end-length`）の 2 本。
 * 描画・ヒット判定・DNA 読み出しはすべてこれを通す。
 */
export function resolveSpans(
  interval: Interval,
  genomeLength: number,
  topology: Topology = 'linear'
): Interval[] {
  if (genomeLength <= 0) return [];
  const rawStart = Math.floor(interval.start);
  const rawEnd = Math.ceil(interval.end);
  if (rawEnd <= rawStart) return [];

  if (topology !== 'circular') {
    const start = Math.max(0, Math.min(rawStart, genomeLength));
    const end = Math.max(0, Math.min(rawEnd, genomeLength));
    return end > start ? [{ start, end }] : [];
  }

  const length = Math.min(rawEnd - rawStart, genomeLength);
  const start = normalizeBase(rawStart, genomeLength, 'circular');
  const end = start + length;
  if (end <= genomeLength) return [{ start, end }];
  return [
    { start, end: genomeLength },
    { start: 0, end: end - genomeLength },
  ];
}

/** スパン合計長 */
export function spansLength(spans: Interval[]): number {
  return spans.reduce((sum, span) => sum + intervalLength(span), 0);
}

/** ある塩基位置が区間に含まれるか（原点ラップ込み） */
export function intervalContains(
  interval: Interval,
  position: number,
  genomeLength: number,
  topology: Topology = 'linear'
): boolean {
  return resolveSpans(interval, genomeLength, topology).some(
    (span) => position >= span.start && position < span.end
  );
}

/** 2 区間が重なるか（原点ラップ込み） */
export function intervalsOverlap(
  a: Interval,
  b: Interval,
  genomeLength: number,
  topology: Topology = 'linear'
): boolean {
  const spansA = resolveSpans(a, genomeLength, topology);
  const spansB = resolveSpans(b, genomeLength, topology);
  return spansA.some((sa) => spansB.some((sb) => sa.start < sb.end && sb.start < sa.end));
}

const NUMBER_FORMAT = new Intl.NumberFormat('en-US');

/** 座標ラベル。桁が多い染色体級でも読めるよう桁区切りを入れる。 */
export function formatBase1Based(position0Based: number): string {
  return NUMBER_FORMAT.format(position0Based + 1);
}

/**
 * 表示用の範囲文字列。原点ラップは `111–222 · 1–40`（§3.3）。
 */
export function formatIntervalRange1Based(interval: Interval, genomeLength: number): string {
  const topology: Topology = isOriginWrapped(interval, genomeLength) ? 'circular' : 'linear';
  const spans = resolveSpans(interval, genomeLength, topology);
  if (spans.length === 0) return '';
  return spans
    .map((span) => `${formatBase1Based(span.start)}–${NUMBER_FORMAT.format(span.end)}`)
    .join(' · ');
}
